import { safeStorage } from "../lib/safeStorage.mjs";
import { PaymentMethodType, TaxType } from "@aitisal/contracts/common";
import hash from "object-hash";
import { useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

//#region src/hooks/useCart.ts
const DEFAULT_CART_KEY = "default";
const initialState = {
	carts: {},
	selectedPaymentMethod: PaymentMethodType.CASH
};
const cartStore = create()(persist((set) => ({
	discount: {
		type: "PERCENTAGE",
		amount: "",
		isDiscountApplied: false
	},
	carts: {},
	selectedPaymentMethod: PaymentMethodType.CASH,
	setCart: (key, items) => {
		set((state) => ({ carts: {
			...state.carts,
			[key]: items
		} }));
	},
	setSelectedPaymentMethod: (method) => {
		set({ selectedPaymentMethod: method });
	},
	setDiscount: (discount) => {
		set({ discount });
	},
	clearCartBucket: (key) => {
		set((state) => ({ carts: {
			...state.carts,
			[key]: []
		} }));
	},
	resetCart: (key) => {
		if (key === void 0) set(initialState);
		else set((state) => ({
			carts: {
				...state.carts,
				[key]: []
			},
			selectedPaymentMethod: PaymentMethodType.CASH
		}));
	}
}), {
	name: "cart-v2",
	storage: createJSONStorage(() => safeStorage)
}));
function useCart({ restaurant, cartKey } = {}) {
	const { carts, setCart: setCartBucket, resetCart: resetCartBucket, clearCartBucket, discount, setDiscount, selectedPaymentMethod, setSelectedPaymentMethod } = cartStore();
	const key = cartKey ?? DEFAULT_CART_KEY;
	const isKeyed = cartKey !== void 0;
	const cart = carts?.[key] || [];
	const setCart = (items) => setCartBucket(key, items);
	const resetCart = () => isKeyed ? resetCartBucket(key) : resetCartBucket();
	const clearCart = (k = key) => clearCartBucket(k);
	const getCurrentCart = (k = key) => carts?.[k] || [];
	function addToCart(item, customizations, quantity, variant) {
		const itemHashId = getCartItemHashId(item.itemId, customizations || [], variant?.variantId);
		const quantityToAdd = Number(quantity) || 1;
		if (isInCart(itemHashId)) setCart(cart.map((cartItem) => {
			if (cartItem.hashId === itemHashId) {
				const newQuantity = cartItem.quantity + quantityToAdd;
				cartItem.quantity = Math.max(0, newQuantity);
			}
			return cartItem;
		}).filter((cartItem) => cartItem.quantity > 0));
		else if (quantityToAdd > 0) {
			const basePrice = typeof item.itemPrice === "number" ? item.itemPrice : item.price || 0;
			const newItem = {
				...item,
				isOldOrderItem: false,
				customizations,
				quantity: quantityToAdd,
				itemPrice: basePrice,
				price: calculateItemPrice(basePrice, customizations),
				hashId: itemHashId,
				variantId: variant?.variantId,
				variantSku: variant?.sku,
				variantBarcode: variant?.barcode,
				variantName: variant?.name
			};
			setCart([...cart, newItem]);
		}
	}
	function onDecrement(hashId) {
		setCart(cart.map((item) => {
			if (item.hashId === hashId) item.quantity = item.quantity - 1;
			return item;
		}));
	}
	function onIncrement(hashId) {
		setCart(cart.map((item) => {
			if (item.hashId === hashId) item.quantity = item.quantity + 1;
			return item;
		}));
	}
	function isInCart(hashId) {
		return cart.some((v) => v.hashId === hashId);
	}
	function removeFromCart(hashId) {
		setCart(cart.filter((v) => v.hashId !== hashId));
	}
	function updateCartItem(oldHashId, item, customizations, quantity, variant) {
		const newHashId = getCartItemHashId(item.itemId, customizations || [], variant?.variantId);
		const basePrice = typeof item.itemPrice === "number" ? item.itemPrice : item.price || 0;
		if (cart.find((cartItem) => cartItem.hashId === newHashId && cartItem.hashId !== oldHashId)) setCart(cart.map((cartItem) => {
			if (cartItem.hashId === newHashId) return {
				...cartItem,
				quantity: cartItem.quantity + quantity
			};
			return cartItem;
		}).filter((cartItem) => cartItem.hashId !== oldHashId));
		else setCart(cart.map((cartItem) => {
			if (cartItem.hashId === oldHashId) return {
				...item,
				isOldOrderItem: false,
				customizations,
				quantity,
				itemPrice: basePrice,
				price: calculateItemPrice(basePrice, customizations),
				hashId: newHashId,
				variantId: variant?.variantId,
				variantSku: variant?.sku,
				variantBarcode: variant?.barcode,
				variantName: variant?.name
			};
			return cartItem;
		}));
	}
	/**
	* Identity of a cart line: itemId + customizations + variant.
	*
	* When no variant is selected the hash input is `{ item, customizations }`
	* WITHOUT a variantId key — byte-for-byte the pre-variant computation. This
	* keeps hashIds on carts persisted before variants shipped mergeable (a
	* re-added product increments the existing line instead of duplicating it).
	* With a variant, the key is included so different variants of one item are
	* distinct lines.
	*/
	function getCartItemHashId(itemId, customizations, variantId) {
		return variantId ? hash({
			item: itemId,
			customizations,
			variantId
		}) : hash({
			item: itemId,
			customizations
		});
	}
	function calcOptionsPrice(options) {
		return (options || []).reduce((prev, curr) => prev + (Number(curr.price) || 0) * (Number(curr.quantity) || 1), 0);
	}
	function calcCustomizationsPrice(customizations) {
		return (customizations || []).reduce((prev, curr) => prev + calcOptionsPrice(curr.customizationOptions), 0);
	}
	function calculateItemPrice(price, customizations) {
		return (Number(price) || 0) + calcCustomizationsPrice(customizations);
	}
	const totalPrice = useMemo(() => {
		return cart.reduce((prev, curr) => prev + (Number(curr.price) || 0) * (Number(curr.quantity) || 0), 0);
	}, [cart]);
	function calculateTax(totalPrice, taxType, taxRate, paymentMethodTaxRate) {
		const safeTotalPrice = Number(totalPrice) || 0;
		const safeTaxRate = Number(taxRate) || 0;
		const safePaymentMethodTaxRate = Number(paymentMethodTaxRate) || 0;
		const effectiveTaxRate = safePaymentMethodTaxRate > 0 ? safePaymentMethodTaxRate : safeTaxRate;
		if (taxType === TaxType.FLAT) return effectiveTaxRate;
		return safeTotalPrice * effectiveTaxRate / 100;
	}
	const isPriceInclusiveOfTax = !!restaurant?.settings?.tax?.isPriceInclusiveOfTax;
	function extractTax(inclusivePrice, taxRate) {
		const safePrice = Number(inclusivePrice) || 0;
		const safeRate = Number(taxRate) || 0;
		if (safePrice === 0 || safeRate === 0) return 0;
		return Math.floor(safePrice * safeRate / (100 + safeRate));
	}
	return {
		cart,
		carts,
		discount,
		setDiscount,
		totalPrice,
		setCart,
		totalTax: useMemo(() => {
			const paymentMethodTaxRate = selectedPaymentMethod === PaymentMethodType.CASH ? restaurant?.settings?.tax?.rateOnCash : restaurant?.settings?.tax?.rateOnCard;
			if (isPriceInclusiveOfTax) return extractTax(totalPrice, paymentMethodTaxRate || 0) / 100;
			let totalItemTax = 0;
			cart?.forEach((item) => {
				if (item?.type === "custom" && !item?.isTaxable) return;
				const itemTax = calculateTax(item?.itemPrice / 100 * item?.quantity, item?.tax?.type, item?.tax?.rate || 0, paymentMethodTaxRate);
				totalItemTax += itemTax;
				item?.customizations?.forEach((customization) => {
					if (customization.isOptional) customization?.customizationOptions?.forEach((option) => {
						const optionTax = calculateTax(option?.price / 100 * (option?.quantity || 1), option?.itemTax?.type, option?.itemTax?.rate || 0, paymentMethodTaxRate);
						totalItemTax += optionTax;
					});
					else customization?.customizationOptions?.forEach((option) => {
						const optionTax = calculateTax(option?.price / 100 * (option?.quantity || 1), item?.tax?.type, item?.tax?.rate || 0, paymentMethodTaxRate);
						totalItemTax += optionTax;
					});
				});
			});
			return totalItemTax;
		}, [
			cart,
			selectedPaymentMethod,
			restaurant,
			isPriceInclusiveOfTax,
			totalPrice
		]),
		isPriceInclusiveOfTax,
		addToCart,
		resetCart,
		clearCart,
		calculateItemPrice,
		onDecrement,
		onIncrement,
		getCartItemHashId,
		removeFromCart,
		updateCartItem,
		getCurrentCart,
		selectedPaymentMethod,
		setSelectedPaymentMethod
	};
}

//#endregion
export { cartStore, useCart };
//# sourceMappingURL=useCart.mjs.map